import { useEffect } from 'preact/hooks';
import type { RefObject } from 'preact';

const DEFAULT_MIN = 320;

/** Where the scroller's content ends: the lowest outer (margin) edge among its
 *  TOP-LEVEL boxes, stepping through `display: contents` wrappers. Not a deep
 *  union — rows hidden inside a scrolling list sit far below its box and would
 *  read as a page that is already overfull. */
function contentBottom(parent: Element): number {
  let bottom = -Infinity;
  for (const child of Array.from(parent.children)) {
    const cs = getComputedStyle(child);
    if (cs.display === 'contents') bottom = Math.max(bottom, contentBottom(child));
    else if (cs.display !== 'none' && cs.position !== 'fixed' && cs.position !== 'absolute') {
      bottom = Math.max(bottom, child.getBoundingClientRect().bottom + parseFloat(cs.marginBottom));
    }
  }
  return bottom;
}

/** The nearest ancestor that scrolls vertically — the shell's `main`. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let a = el.parentElement; a && a !== document.documentElement; a = a.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(a).overflowY)) return a;
  }
  return null;
}

/** Grows (or shrinks) `ref`'s element so the PAGE exactly fills its scroll
 *  area: the list ends where the screen does, on a laptop, a 27" monitor or a
 *  TV alike, with whatever sits below it (a count, the page's padding) still
 *  in view. Lifted out of `Table`'s own `fill` prop (R-230-class pages: a list
 *  of repeated rows, not a table) so any scrolling list can match it rather
 *  than re-deriving a weaker `vh`-based approximation. A fixed
 *  `calc(100dvh - Npx)` could only be right for one layout — it left a
 *  constant band under the list and, on a tall screen, the rows ran out long
 *  before it. Never below `min`, so a phone's long stacked header scrolls the
 *  page instead.
 *
 *  `prop: 'maxHeight'` CAPS the element at that size instead of forcing it: a
 *  short list keeps its natural height (no tall empty card), a long one stops
 *  at the screen's bottom and scrolls inside. */
export function useFillHeight(
  ref: RefObject<HTMLElement>,
  enabled: boolean,
  min = DEFAULT_MIN,
  prop: 'height' | 'maxHeight' = 'height',
) {
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    const sc = el && scrollParent(el);
    if (!el || !sc) return;
    let frame = 0;
    const fit = () => {
      // `scrollHeight` cannot say how much room is LEFT (it never drops below the
      // box), so measure where the content ends against the scroller's inner
      // bottom edge (inside its padding).
      const box = sc.getBoundingClientRect();
      const innerBottom =
        box.top + sc.clientTop + sc.clientHeight - parseFloat(getComputedStyle(sc).paddingBottom);
      const current = el.getBoundingClientRect().height;
      const next = Math.max(min, Math.floor(current + (innerBottom - contentBottom(sc))));
      // A cap is compared with the cap already set, not with the box: a short list
      // sits below its cap for good, and re-writing it would only churn.
      const was = prop === 'maxHeight' ? parseFloat(el.style.maxHeight) || 0 : current;
      if (Math.abs(next - was) > 1) el.style[prop] = `${next}px`;
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    };
    fit();
    // The viewport (a window drag to another screen, a phone rotating) and the
    // page around the list (a notice appearing above it) both move the answer.
    // Every box from the list up to the scroller: one of them grows when
    // anything around the list changes (a `display: contents` wrapper never
    // reports a size, so observing the scroller's children alone missed that).
    const ro = new ResizeObserver(schedule);
    for (let a: HTMLElement | null = el.parentElement; a; a = a === sc ? null : a.parentElement)
      ro.observe(a);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      el.style[prop] = '';
    };
  }, [enabled, ref, min, prop]);
}
